import Dexie from 'dexie';
import type { Table } from 'dexie';
import type { Preset } from '../../core/types';
import { SERVER_PROVIDER_ID } from '../../core/providers/ids';
import type { VoiceMode } from './contracts';
import { createLocalReference } from './storage';
import type { LocalReference } from './storage';

export interface RoleVoiceProfile {
  scope: string;
  designDescription: string;
  cloneDescription: string;
  reference?: LocalReference;
}

export class RoleVoiceDatabase extends Dexie {
  profiles!: Table<RoleVoiceProfile, string>;
  constructor(name = 'zhixin-role-voices-device-v1') {
    super(name);
    this.version(1).stores({ profiles: 'scope' });
  }
}

/** Deliberately separate from application/backup stores: these secrets never sync. */
export class RoleVoiceStore {
  private tail: Promise<unknown> = Promise.resolve();
  private failedScopes = new Set<string>();
  constructor(readonly db = new RoleVoiceDatabase()) {}
  private scope(userId: string | null, presetId: string): string { return JSON.stringify([userId, presetId]); }
  async read(userId: string | null, presetId: string): Promise<RoleVoiceProfile | undefined> {
    await this.tail;
    const scope = this.scope(userId, presetId);
    if (this.failedScopes.has(scope)) throw new Error('角色音色配置尚未成功保存在此设备');
    return this.db.profiles.get(scope);
  }
  patch(userId: string | null, presetId: string, patch: Partial<Omit<RoleVoiceProfile, 'scope'>>): Promise<void> {
    const scope = this.scope(userId, presetId);
    const write = this.tail.then(async () => {
      const current = await this.db.profiles.get(scope);
      await this.db.profiles.put({ scope, designDescription: '', cloneDescription: '', ...current, ...patch });
      this.failedScopes.delete(scope);
    }).catch((error: unknown) => { this.failedScopes.add(scope); throw error; });
    this.tail = write.catch(() => undefined);
    return write;
  }
  async saveReference(userId: string | null, presetId: string, file: File, consent: boolean): Promise<LocalReference> {
    const reference = await createLocalReference(userId ?? 'local', file, consent);
    await this.patch(userId, presetId, { reference });
    return reference;
  }
  remove(userId: string | null, presetId: string): Promise<void> {
    const scope = this.scope(userId, presetId);
    const write = this.tail.then(async () => { await this.db.profiles.delete(scope); this.failedScopes.delete(scope); });
    this.tail = write.catch(() => undefined);
    return write;
  }
}

export function roleVoiceMode(preset: Pick<Preset, 'ttsVoiceMode' | 'ttsModel'>): VoiceMode {
  if (preset.ttsVoiceMode) return preset.ttsVoiceMode;
  if (/voice[-_]?design|voicedesign/i.test(preset.ttsModel ?? '')) return 'design';
  if (/voice[-_]?clone|voiceclone/i.test(preset.ttsModel ?? '')) return 'clone';
  return 'preset';
}

export function roleVoiceError(mode: VoiceMode, profile?: RoleVoiceProfile, autoVoiceDesign = false): string | null {
  if (!autoVoiceDesign && mode === 'design' && !profile?.designDescription.trim()) return '请填写当前角色的专属音色描述词';
  if (!autoVoiceDesign && mode === 'clone' && !profile?.cloneDescription.trim()) return '请填写当前角色的专属参考音频描述';
  if (mode === 'clone' && !profile?.reference?.consent) return '请为当前角色添加专属参考音频并确认音色授权';
  return null;
}

export function roleVoiceProviderError(preset: Pick<Preset, 'ttsProviderId' | 'ttsVoiceMode' | 'ttsModel'>): string | null {
  return preset.ttsProviderId && preset.ttsProviderId !== SERVER_PROVIDER_ID && roleVoiceMode(preset) !== 'preset'
    ? '此自定义语音提供商不支持音色设计或音色复刻，请选择官方语音'
    : null;
}

export const roleVoiceStore = new RoleVoiceStore();
